import { Effect, Reducer } from '@umijs/max';
import { notification, Modal } from 'antd';

import { Loan, LoanType } from '@/interfaces/loans';

import { searchFiles } from '@/services/files.service';
import { checkLoanLimit } from '@/services/affiliates.service';
import { getLoanDefaults, getLoanTypes, createLoan } from '@/services/loans.service';

export interface StateType {
  files: File[];
  current?: string;
  success: boolean;
  loan: Loan | null;
  file: File | null;
  submitting: boolean;
  canRequest: boolean;
  loanTypes: LoanType[];
  isFetchingFiles: boolean;
  defaults: any;
  step?: any;
  loanType: any;
}

export interface ModelType {
  namespace: string;
  state: StateType;

  effects: {
    reset: Effect;
    setFile: Effect;
    getFiles: Effect;
    setLoanType: Effect;
    getLoanTypes: Effect;
    submitStepForm: Effect;
    canRequestLoan: Effect;
    clearRequestLoan: Effect;
  };

  reducers: {
    saveFile: Reducer<any>;
    saveFiles: Reducer<any>;
    resetState: Reducer<any>;
    setSuccess: Reducer<any>;
    saveLoanType: Reducer<any>;
    setLoanTypes: Reducer<any>;
    setSubmitting: Reducer<any>;
    saveCurrentStep: Reducer<any>;
    setLoadingFiles: Reducer<any>;
    saveStepFormData: Reducer<any>;
    setCanRequestLoan: Reducer<any>;
  };
}

const Model: ModelType = {
  namespace: 'newLoan',

  state: {
    current: 'general',

    defaults: null,

    loanTypes: [],
    loanType: null,

    files: [],
    file: null,
    isFetchingFiles: false,

    canRequest: true,

    step: {},

    loan: null,
    success: false,
    submitting: false,
  },

  effects: {
    *submitStepForm(_, { call, put, select }) {
      const data = yield select(({ newLoan }: { newLoan: StateType }) => newLoan.step);
      yield put({
        type: 'setSubmitting',
        payload: true,
      });
      try {
        const { file, affiliate, user, ...fields } = data;

        const payload = {
          ...fields,
          file: data.file.key,
          locality: data.locality.city.key,
          guarantor_locality: data.geographic.city.key,
          start_date: data.start_date.format('YYYY-MM-DD'),
          expiration_date: data.expiration_date.format('YYYY-MM-DD'),
          amount: data.amount.replace('$', '').replaceAll('.', '').replace(',', '.').trim(),
          extra_interest: data.extra_interest
            ? data.extra_interest.replace('%', '').replace(',', '.').trim()
            : 0,
        };
        const result = yield call(createLoan, payload);

        yield put({
          type: 'setSuccess',
          payload: result,
        });
      } catch (error: any) {
          if (error?.response?.data && error?.response?.data?.non_field_errors) {
            Modal.error({
              centered: true,
              title: 'Lo sentimos',
              content: error?.response?.data?.non_field_errors[0],
            });
        } else {
          notification.error({ message: error.response.data.detail || 'Ocurrió un error.' });
        }
      }

      yield put({
        type: 'setSubmitting',
        payload: false,
      });
    },

    *setFile({ payload }, { put }) {
      yield put({
        type: 'saveFile',
        payload,
      });
    },

    *setLoanType({ payload }, { put }) {
      yield put({
        type: 'saveLoanType',
        payload,
      });
    },

    *reset(_, { put }) {
      yield put({
        type: 'resetState',
      });
    },

    *canRequestLoan(_, { call, select, put, all }) {
      const file = yield select(({ newLoan }: { newLoan: StateType }) => newLoan.file);
      const loanType = yield select(({ newLoan }: { newLoan: StateType }) => newLoan.loanType);

      if (file && loanType) {
        const [canRequest] = yield all([call(checkLoanLimit, file.affiliate.id, loanType)]);
        yield put({
          type: 'setCanRequestLoan',
          payload: canRequest,
        });
      } else {
        yield put({
          type: 'setCanRequestLoan',
          payload: true,
        });
      }
    },

    *clearRequestLoan(_, { put }) {
      yield put({
        type: 'setCanRequestLoan',
        payload: true,
      });
    },

    *getLoanTypes({ payload }, { call, put, all }) {
      const [loanTypes, defaults] = yield all([
        call(getLoanTypes, payload),
        call(getLoanDefaults, payload),
      ]);
      yield put({
        type: 'setLoanTypes',
        payload: { loanTypes, defaults },
      });
    },

    *getFiles({ payload }, { call, put }) {
      yield put({
        type: 'setLoadingFiles',
        payload: true,
      });

      try {
        const response = yield call(searchFiles, payload);
        yield put({
          type: 'saveFiles',
          payload: response,
        });
      } catch (error) {
        yield put({
          type: 'setLoadingFiles',
          payload: false,
        });
      }
    },
  },

  reducers: {
    resetState() {
      return {
        step: {},
        files: [],
        loan: null,
        file: null,
        loanTypes: [],
        success: false,
        canRequest: true,
        submitting: false,
        current: 'general',
        isFetchingFiles: false,
      };
    },

    setCanRequestLoan(state, { payload }) {
      return {
        ...state,
        canRequest: payload,
      };
    },

    saveCurrentStep(state, { payload }) {
      return {
        ...state,
        current: payload,
      };
    },

    setLoanTypes(state, { payload }) {
      return {
        ...state,
        ...payload,
        loanTypes: payload.loanTypes.filter((loan: LoanType) => loan.enabled),
      };
    },

    saveFile(state, { payload }) {
      return { ...state, file: payload };
    },

    saveLoanType(state, { payload }) {
      return { ...state, loanType: payload };
    },

    saveFiles(state, { payload }) {
      return { ...state, files: payload.results, isFetchingFiles: false };
    },

    setLoadingFiles(state, { payload }) {
      return { ...state, isFetchingFiles: payload };
    },

    setSubmitting(state, { payload }) {
      return { ...state, submitting: payload };
    },

    setSuccess(state, { payload }) {
      return { ...state, success: true, loan: payload };
    },

    saveStepFormData(state, { payload }) {
      return {
        ...state,
        step: {
          ...(state as StateType).step,
          ...payload,
        },
      };
    },
  },
};

export default Model;
